// Fires the digital-menu qr-scan API exactly once per QR scan.
//
// A "QR scan" is a fresh landing on the menu route with restaurant_id +
// table_id in the URL query params. In-app navigation to the menu restores
// these from sessionStorage (no URL params), so it does NOT trigger the call.
//
// Gating:
//   - V1 only: the native app fires the call on V2, so the portal skips it when
//     running V2 in-app. mweb and V1 in-app fire (!isV2InApp). Config must be
//     loaded first so the V1/V2 decision is reliable.
//   - Logged-in only: logged-out scans do nothing; after the post-login
//     forwardPath redirect reloads the menu (query params intact), the user is
//     logged in and the call fires then.

import { useContext, useEffect, useRef } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { SwiggyContextHeader, useApiContextFetch, useDigitalMenuConfig } from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";

import { recordQrScan } from "../api/qrScan";
import { NR_TABLES, ROUTES } from "../constants";
import { useIsV2InApp } from "./useIsV2InApp";
import { isUserLoggedIn } from "../utils/auth";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

export const useQrScanTracking = () => {
    const router = useRouter();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { isConfigLoaded } = useDigitalMenuConfig();
    const { isV2InApp } = useIsV2InApp();
    const makeApiCall = useApiContextFetch();
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (isEmpty(router?.isReady) || isEmpty(makeApiCall) || hasFiredRef.current) return;

        if (router?.pathname !== ROUTES.MENU) return;

        const restaurantId = (router.query.restaurant_id as string) || "";
        const tableId = (router.query.table_id as string) || "";
        if (isEmpty(restaurantId) || isEmpty(tableId)) return;

        if (isEmpty(isConfigLoaded)) return;

        if (isV2InApp) return;

        if (!isUserLoggedIn(swiggyHeaders)) return;

        hasFiredRef.current = true;

        recordQrScan({ restaurantId, tableId }, makeApiCall).catch(() => {
            sendCustomNREvViaBlaze(NR_TABLES.QR_SCAN_API_ERROR, { restaurantId, tableId }, swiggyHeaders);
        });
    }, [router?.isReady, router?.pathname, router?.query, isConfigLoaded, isV2InApp, makeApiCall, swiggyHeaders]);
};
